import { useRef, useState } from 'react';
import { Autoplay } from 'swiper/modules';
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react';

import type { HeroFullOverlayContent } from '@/components/TopLevelComponents/HeroFullOverlay/HeroFullOverlay';
import useViewPortSize from '@/hooks/useViewportSize';
import type { HeroCarouselCF } from '@/types';

import useStyles from './HeroCarousel.styles';
import ProgressOverlay from './ProgressOverlay/ProgressOverlay';
import HeroCarouselSlide from './Slide/Slide';

import 'swiper/css';

const DEFAULT_SLIDE_TIMING = 5_000;

interface Props {
  content: HeroCarouselCF;
}

export const getSlideDuration = (slide?: HeroFullOverlayContent) => {
  if (slide?.fields.carouselSlideTiming) {
    return slide.fields.carouselSlideTiming * 1000;
  }

  return DEFAULT_SLIDE_TIMING;
};

export default function HeroCarousel({ content }: Props) {
  const { classes, cx } = useStyles();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const swiperRef = useRef<SwiperClass>(null);
  const { viewportSize } = useViewPortSize();

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveSlideIndex(swiper.realIndex);
  };

  const handleProgressBarClick = (index: number) => {
    swiperRef.current?.slideToLoop(index);
  };

  const handleSwiperInit = (swiper: SwiperClass) => {
    swiperRef.current = swiper;
  };

  const slides = (content.fields.slides ||
    []) as unknown as HeroFullOverlayContent[];

  return (
    <Swiper
      loop
      autoplay
      key={viewportSize}
      modules={[Autoplay]}
      onSlideChange={handleSlideChange}
      onSwiper={handleSwiperInit}
      className={cx(classes.container)}
    >
      {slides.map((slide) => (
        <SwiperSlide
          key={slide?.sys.id}
          data-swiper-autoplay={getSlideDuration(slide)}
        >
          <HeroCarouselSlide slide={slide} />
        </SwiperSlide>
      ))}

      <ProgressOverlay
        slides={slides}
        activeSlideIndex={activeSlideIndex}
        onProgressBarClick={handleProgressBarClick}
      />
    </Swiper>
  );
}
